import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";

import useAPI from "services/Api";
import AuthHeader from "pages/AuthHeader";
import Input, { InputRef } from "input/Input";
import Page from "pages/Page";
import { navigate } from "utility/NavigationService";
import { useNotificationManager } from "notifications/NotificationManager";

const Login = ({ componentName = "[Login]" }) => {
    const [loginText, setLoginText] = useState("Sign in");
    const [password, setPassword] = useState<string | null>(null);
    const [username, setUsername] = useState<string | null>(null);

    // Refs for Input validation
    const usernameInputRef = useRef<InputRef>(null);
    const passwordInputRef = useRef<InputRef>(null);

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const location = useLocation();
    const shownResetBannerRef = useRef(false);

    // ---=== Utilities ===--- //

    const usernameChanged = (value: string) => {
        setLoginText("Sign in");
        setUsername(value);
    };

    const passwordChanged = (value: string) => {
        setLoginText("Sign in");
        setPassword(value);
    };

    const validateForm = (): boolean => {
        notificationManager.clearAllNotifications();

        const isUsernameValid = usernameInputRef.current?.validate() ?? false;
        const isPasswordValid = passwordInputRef.current?.validate() ?? false;

        return isUsernameValid && isPasswordValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoginText("Signing in");

        void API.api_Login(
            componentName,
            username,
            password,
            onLoginSuccess,
            onLoginFail
        );
    };

    const handleSubmitButton = async (e) => {
        void handleSubmit(e);
    };

    const handleKeyPress = async (e) => {
        if (e.key === "Enter") {
            void handleSubmit(e);
        }
    };

    const onLoginSuccess = (userData) => {
        let roleType = userData.user.role_type.toLowerCase();
        if (roleType.includes("admin") || roleType === "physician") {
            void navigate("/reportsExternal");
        }
        else {
            void navigate("/");
        }
    };

    const onLoginFail = (message) => {
        setLoginText("Sign in");
        notificationManager.addError(message);
    };

    // ---=== useEffects ===--- //

    // Show success banner if redirected after password reset (once)
    useEffect(() => {
        if (shownResetBannerRef.current) return;

        const state = location.state as any | undefined;
        if (state?.passwordResetSuccess) {
            shownResetBannerRef.current = true;
            notificationManager.addSuccess("Your password has been reset. Please sign in.");
            // Clear history state so refresh doesn't re-show the banner
            window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        }
    }, []);

    // ---=== Render ===--- //

    return (
        <Page componentName={componentName} internal={false}>
            <div className="h-screen flex items-center justify-center pb-12">
                <div className="w-96 h-96 flex-col justify-center content-center items-center gap-8 inline-flex">
                    <AuthHeader showWelcome />
                    <form className="w-full max-w-sm" onSubmit={handleSubmitButton} onKeyUp={(e) => handleKeyPress(e)}>
                        <div className="self-stretch h-64 rounded-xl flex-col justify-start items-center gap-6 flex" id="form">
                            <div className="self-stretch h-40 flex-col justify-start items-start gap-5 flex">
                                <div className="self-stretch h-16 flex-col justify-start items-start gap-1.5 flex">
                                    <div className="self-stretch h-16 flex-col justify-start items-start gap-1.5 flex">
                                        <div className="text-slate-700 text-sm font-medium leading-tight">Email</div>
                                        <Input
                                            ref={usernameInputRef}
                                            className={"shadow appearance-none border rounded-lg w-full py-2.5 px-3.5 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"}
                                            name="Email"
                                            noLabel
                                            required
                                            type="text"
                                            validations={[{ type: "email", value: username }]}
                                            value={username ?? ""}
                                            onChange={usernameChanged}
                                        />
                                    </div>
                                </div>
                                <div className="self-stretch h-16 flex-col justify-start items-start gap-1.5 flex">
                                    <div className="self-stretch h-16 flex-col justify-start items-start gap-1.5 flex">
                                        <div className="text-slate-700 text-sm font-medium leading-tight">Password</div>
                                        <Input
                                            ref={passwordInputRef}
                                            className={"shadow appearance-none border rounded-lg w-full py-2.5 px-3.5 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"}
                                            name="Password"
                                            noLabel
                                            required
                                            type="password"
                                            value={password ?? ""}
                                            onChange={passwordChanged}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="self-stretch justify-center items-center inline-flex">
                                <a href="/password/reset" className="text-brand-600 text-sm font-semibold leading-tight">
                                    Forgot password
                                </a>
                            </div>
                            <div className="self-stretch h-11 flex-col justify-start items-start gap-4 flex">
                                <button type="submit" className="self-stretch px-4 py-2.5 bg-brand-600 hover:bg-brand-700 rounded-lg shadow justify-center items-center gap-1.5 inline-flex cursort-pointer">
                                    <div className="px-0.5 justify-center items-center flex">
                                        <div className="text-white text-base font-semibold leading-normal">{loginText}</div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Page>
    );
};

export default Login;